import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { searchAPI } from '../services/static-api';
import { useAnalytics } from '../services/analytics';
import debounce from 'lodash.debounce';

function SearchBar({ onSubmit, placeholder = "Search restaurants...", compact = false, onCancel = null, autoFocus = false }) {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  
  const queryInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const suggestionRefs = useRef([]);
  const { trackFeatureUsage, trackSearch } = useAnalytics();

  // Debounced search suggestions
  const debouncedFetchSuggestions = useRef(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await searchAPI.suggestions(searchQuery);
        setSuggestions(response.data.suggestions || []);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    debouncedFetchSuggestions(query);
  }, [query, debouncedFetchSuggestions]);

  // Get user's current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Geolocation error:', error);
        },
        { timeout: 5000 }
      );
    }
  }, []);

  // Auto-focus on mount if specified
  useEffect(() => {
    if (autoFocus && queryInputRef.current) {
      queryInputRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!query.trim() && !location.trim()) return;

    const searchData = {
      query: query.trim() || undefined,
      location: location.trim() || undefined,
      ...(userLocation && !location.trim() && {
        lat: userLocation.lat,
        lng: userLocation.lng
      })
    };

    trackSearch(query, { location: location || 'current' }, 0);
    onSubmit(searchData);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'restaurant') {
      setQuery(suggestion.text);
      if (suggestion.location) {
        setLocation(suggestion.location);
      }
    } else if (suggestion.type === 'city') {
      setLocation(suggestion.text);
    } else if (suggestion.type === 'cuisine') {
      setQuery(suggestion.text);
    }

    trackFeatureUsage('search_suggestions');
    setShowSuggestions(false);
    
    // Auto-submit if we have enough info
    setTimeout(() => {
      const searchData = {
        query: suggestion.type === 'restaurant' || suggestion.type === 'cuisine' ? suggestion.text : query,
        location: suggestion.type === 'city' ? suggestion.text : (suggestion.location || location)
      };
      onSubmit(searchData);
    }, 100);
  };

  const handleUseCurrentLocation = () => {
    if (userLocation) {
      setLocation('Current location');
      trackFeatureUsage('auto_location');
    } else if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(newLocation);
          setLocation('Current location');
          setIsLoading(false);
          trackFeatureUsage('auto_location');
        },
        (error) => {
          console.error('Geolocation failed:', error);
          setIsLoading(false);
        }
      );
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : -1
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => 
          prev > -1 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          e.preventDefault();
          handleSuggestionClick(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  // Effect to scroll active suggestion into view
  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [activeIndex]);

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className={`${compact ? 'flex' : 'space-y-3 sm:space-y-0 sm:flex'} gap-2`}>
        {/* Search Query Input */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={queryInputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`form-search ${compact ? 'py-2' : 'py-3'} w-full focus:shadow-card-hover`}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                queryInputRef.current?.focus();
              }}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-surface-400 hover:text-surface-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Location Input */}
        <div className="relative flex-1 max-w-xs">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MapPinIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            ref={locationInputRef}
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="City or 'Current location'"
            className={`form-input pl-10 pr-10 ${compact ? 'py-2' : 'py-3'} w-full`}
          />
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={isLoading}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            title="Use current location"
          >
            <svg 
              className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
              />
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
              />
            </svg>
          </button>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || (!query.trim() && !location.trim())}
          className="btn-primary flex-shrink-0"
        >
          {isLoading ? (
            <div className="loading-spinner" />
          ) : (
            <>
              <MagnifyingGlassIcon className="h-4 w-4" />
              {!compact && <span className="ml-2">Search</span>}
            </>
          )}
        </button>

        {/* Cancel Button (Mobile) */}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary flex-shrink-0 md:hidden"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </form>

      {/* Search Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.type}-${index}`}
              ref={el => suggestionRefs.current[index] = el}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors ${
                activeIndex === index ? 'bg-gray-50' : ''
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {suggestion.type === 'restaurant' && (
                    <div className="w-6 h-6 bg-blue-100 rounded text-blue-600 flex items-center justify-center text-xs font-medium">
                      R
                    </div>
                  )}
                  {suggestion.type === 'city' && (
                    <MapPinIcon className="w-5 h-5 text-gray-400" />
                  )}
                  {suggestion.type === 'cuisine' && (
                    <div className="w-6 h-6 bg-green-100 rounded text-green-600 flex items-center justify-center text-xs font-medium">
                      C
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {suggestion.text}
                  </div>
                  {suggestion.location && suggestion.type === 'restaurant' && (
                    <div className="text-sm text-gray-500 truncate">
                      {suggestion.location}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-xs text-gray-400 capitalize">
                  {suggestion.type}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Click outside to close suggestions */}
      {showSuggestions && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowSuggestions(false)}
        />
      )}
    </div>
  );
}

export default SearchBar;